import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import type { SubmitHandler } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { RecoveryCodeChallenge, TwoFactorCodeChallenge } from "@/components";
import type { LocationState } from "@/hooks";
import { useAuthentication } from "@/hooks";
import { ROUTES } from "@/router";
import type { TwoFactorAuthFormValuesType } from "@/types";

export const TwoFactorAuthChallenge = () => {
  const [isUsingRecoveryCodes, setIsUsingRecoveryCodes] = useState(false);

  const { useChallengeTwoFactor, useChallengeRecoveryCode } =
    useAuthentication();

  const {
    mutate: challenge2fa,
    isError: hasChallengeFaError,
    isPending: isChallengeLoading,
  } = useChallengeTwoFactor();

  const {
    mutate: challenge2faRecoveryCode,
    isError: hasChallengeRecoveryCodeError,
    isPending: isRecoveryCodeChallengeLoading,
  } = useChallengeRecoveryCode();

  const location = useLocation();
  const navigate = useNavigate();

  const handleChallengeMutationSuccess = () => {
    const state = location.state as LocationState;
    const from = state?.from;

    const redirectTo = from
      ? `${from.pathname ?? ""}${from.search ?? ""}`
      : ROUTES.newIntake;

    void navigate(redirectTo, { replace: true });
  };

  const onSubmit: SubmitHandler<TwoFactorAuthFormValuesType> = (values) => {
    if (isUsingRecoveryCodes) {
      return challenge2faRecoveryCode(values.code, {
        onSuccess: () => handleChallengeMutationSuccess(),
      });
    }
    challenge2fa(values.code, {
      onSuccess: () => handleChallengeMutationSuccess(),
    });
  };

  const handleShowRecoveryCode = () => setIsUsingRecoveryCodes((prev) => !prev);

  return (
    <>
      <Link
        to={ROUTES.login}
        className="relative flex w-full items-center justify-center"
      >
        <ChevronLeft className="absolute left-0 text-neutral-500" size={16} />
        <p className="text-neutral-600">Go back to Log in</p>
      </Link>

      {isUsingRecoveryCodes ? (
        <RecoveryCodeChallenge
          handleShowRecoveryCode={handleShowRecoveryCode}
          onSubmit={onSubmit}
          hasTwoFaError={hasChallengeRecoveryCodeError}
          isLoading={isRecoveryCodeChallengeLoading}
        />
      ) : (
        <TwoFactorCodeChallenge
          isLoadingChallengeCode={isChallengeLoading}
          hasTwoFaError={hasChallengeFaError}
          onSubmit={onSubmit}
          handleShowRecoveryCode={handleShowRecoveryCode}
        />
      )}
    </>
  );
};
